/**
 * Dev-only event log for spam-protection decisions.
 *
 * Every call to checkSpam() (in any form) routes its outcome through
 * recordSpamEvent() which:
 *   1. Logs a structured, tagged line to the console (DEV builds only).
 *   2. Pushes the event onto an in-memory ring buffer (last MAX events).
 *   3. Notifies subscribers so the floating <SpamDebugSummary /> panel
 *      can re-render in real time.
 *
 * This module has no React dependencies on purpose so it can be imported
 * from the hook without creating a render cycle.
 */
import type { SpamCheckResult } from "./useSpamProtection";

export type SpamDecision =
  | "honeypot"
  | "too_fast"
  | "keyword_match"
  | "whitelist_pass"
  | "learned_pass"
  | "no_match_pass";

export type SpamDebugEvent = {
  id: number;
  ts: number;             // epoch ms when the decision was made
  decision: SpamDecision;
  isSpam: boolean;
  source: string;         // form-source tag, falls back to "unknown"
  elapsedMs: number;
  matchedPattern?: string | undefined;
  matchedWhitelist?: string | undefined;
  /** Friendly label for a recognized spelling-variant abbreviation
   *  detected in the raw text (e.g. "NuLu", "J-Town"). */
  matchedVariantLabel?: string | undefined;
  /** Exact substring that triggered the variant detection. */
  matchedVariantText?: string | undefined;
};

const MAX_EVENTS = 25;
const events: SpamDebugEvent[] = [];
const listeners = new Set<() => void>();
let nextId = 1;

const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV;

function decisionFor(r: SpamCheckResult): SpamDecision {
  if (r.isSpam && r.reason) return r.reason;
  if (r.matchedWhitelist) return "whitelist_pass";
  if (r.matchedLearned) return "learned_pass";
  return "no_match_pass";
}

function consoleFor(decision: SpamDecision): (...args: unknown[]) => void {
  if (decision === "no_match_pass" || decision === "whitelist_pass" || decision === "learned_pass") {
    return console.info.bind(console);
  }
  // Blocks → warn so they stand out in DevTools.
  return console.warn.bind(console);
}

export function recordSpamEvent(r: SpamCheckResult): void {
  const decision = decisionFor(r);
  const evt: SpamDebugEvent = {
    id: nextId++,
    ts: Date.now(),
    decision,
    isSpam: r.isSpam,
    source: r.source ?? "unknown",
    elapsedMs: r.elapsedMs,
    matchedPattern: r.matchedPattern,
    matchedWhitelist: r.matchedWhitelist,
    matchedVariantLabel: r.matchedVariantLabel,
    matchedVariantText: r.matchedVariantText,
  };

  // Ring buffer
  events.unshift(evt);
  if (events.length > MAX_EVENTS) events.length = MAX_EVENTS;

  if (isDev) {
    const log = consoleFor(decision);
    const tag = `[spam-debug:${decision}]`;
    const detail: Record<string, unknown> = {
      source: evt.source,
      elapsedMs: evt.elapsedMs,
    };
    if (evt.matchedPattern) detail['matchedPattern'] = evt.matchedPattern;
    if (evt.matchedWhitelist) detail['matchedWhitelist'] = evt.matchedWhitelist;
    if (evt.matchedVariantLabel) {
      detail['variant'] = evt.matchedVariantText
        ? `${evt.matchedVariantLabel} ("${evt.matchedVariantText}")`
        : evt.matchedVariantLabel;
    }
    log(tag, detail);
  }

  for (const l of listeners) l();
}

export function getSpamEvents(): readonly SpamDebugEvent[] {
  return events;
}

export function clearSpamEvents(): void {
  events.length = 0;
  for (const l of listeners) l();
}

export function subscribeSpamEvents(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
